import React, { createContext, lazy, Suspense, useMemo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../apiBase';
const CommunicationDialog = lazy(() => import('../components/communications/CommunicationDialog'));

export const COMMUNICATION_API = `${API_BASE_URL}/api/office-communications`;
export const COMMUNICATION_WRITE = { withCredentials: true, timeout: 25000, headers: { 'X-AuraX-Communications': '1' } };
const Context = createContext({ items: [], unread: 0, refresh: () => {}, openNotice: () => {} });
export const useCommunications = () => useContext(Context);
export function OfficeCommunicationsProvider({ children }) {
  const { user, isImpersonating } = useAuth();
  const identity = ['admin', 'reseller'].includes(user?.role) ? `${user.id}:${user.root_admin_id || user.id}` : '';
  const [state, setState] = useState({ identity: '', items: [], unread: 0 });
  const [opened, setOpened] = useState(null);
  const [queued, setQueued] = useState(null);
  const [error, setError] = useState('');
  const [reading, setReading] = useState(false);
  const generation = useRef(0);
  const busy = useRef(false);
  const fetching = useRef({ active: false, at: 0 });
  const items = useMemo(() => state.identity === identity ? state.items : [], [state, identity]);
  const unread = items.filter(item => !item.read).length;
  const refresh = useCallback(async (force = false) => {
    if (!identity || fetching.current.active || (!force && Date.now() - fetching.current.at < 15000)) return;
    fetching.current = { active: true, at: Date.now() };
    const current = generation.current;
    try {
      const { data } = await axios.get(`${COMMUNICATION_API}/feed`, { withCredentials: true, timeout: 15000 });
      if (current === generation.current) setState({ identity, items: data.items || [] });
    } catch { /* Keep the last valid feed during transient outages. */ }
    finally { if (current === generation.current) fetching.current.active = false; }
  }, [identity]);
  useEffect(() => {
    generation.current += 1; busy.current = false; fetching.current = { active: false, at: 0 }; setOpened(null); setQueued(null); setError(''); setReading(false);
    setState({ identity, items: [] }); refresh();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, 60000);
    const visible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', visible); document.addEventListener('visibilitychange', visible);
    return () => { generation.current += 1; clearInterval(timer); window.removeEventListener('focus', visible); document.removeEventListener('visibilitychange', visible); };
  }, [identity, refresh]);
  useEffect(() => {
    if (user?.role !== 'reseller' || isImpersonating || opened || queued || busy.current) return undefined;
    const candidate = items.find(item => item.popup_due);
    if (!candidate) return undefined;
    const current = generation.current;
    const timer = setInterval(async () => {
      if (busy.current || document.visibilityState !== 'visible' || document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      busy.current = true;
      try {
        const { data } = await axios.post(`${COMMUNICATION_API}/${candidate.id}/popup`, { publication: candidate.publication }, COMMUNICATION_WRITE);
        if (current !== generation.current) return;
        setState(previous => ({ ...previous, items: previous.items.map(item => item.id === candidate.id ? { ...item, popup_due: false } : item) }));
        if (data.claimed) { setError(''); setQueued({ identity, notice: candidate }); }
      } catch { /* The server will offer it again on the next feed refresh. */ }
      finally { if (current === generation.current) busy.current = false; }
    }, 1500);
    return () => clearInterval(timer);
  }, [identity, items, opened, queued, user?.role, isImpersonating]);
  useEffect(() => {
    if (!queued || queued.identity !== identity || opened) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible' && !document.querySelector('[role="dialog"], [role="alertdialog"]')) { setOpened(queued); setQueued(null); }
    }, 250);
    return () => clearInterval(timer);
  }, [queued, identity, opened]);
  function openNotice(notice) {
    setError(''); setOpened({ identity, notice });
    if (notice.popup_due && user?.role === 'reseller' && !isImpersonating) {
      setState(previous => ({ ...previous, items: previous.items.map(item => item.id === notice.id ? { ...item, popup_due: false } : item) }));
      axios.post(`${COMMUNICATION_API}/${notice.id}/popup`, { publication: notice.publication }, COMMUNICATION_WRITE).catch(() => {});
    }
  }
  async function markRead() {
    if (!opened || reading || isImpersonating) return;
    const current = generation.current;
    setReading(true); setError('');
    try {
      await axios.post(`${COMMUNICATION_API}/${opened.notice.id}/read`, { publication: opened.notice.publication }, COMMUNICATION_WRITE);
      if (current !== generation.current) return;
      setState(previous => ({ ...previous, items: previous.items.map(item => item.id === opened.notice.id ? { ...item, read: true, popup_due: false } : item) }));
      setOpened(null);
    } catch { if (current === generation.current) setError('Não foi possível registrar a leitura. Tente novamente.'); }
    finally { if (current === generation.current) setReading(false); }
  }
  return <Context.Provider value={{ items, unread, refresh, openNotice }}>
    {children}
    {opened?.identity === identity && <Suspense fallback={null}><CommunicationDialog notice={opened.notice} onClose={() => setOpened(null)}
      onRead={user?.role === 'reseller' && !isImpersonating ? markRead : undefined} reading={reading} error={error} /></Suspense>}
  </Context.Provider>;
}
